import { useState, useEffect } from 'react'
import { MapPin, Sparkles, Loader2, HelpCircle } from '@/ui/icons'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/ui/components'
import { Button, Input, Label, Textarea } from '@/ui/components'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/ui/components/primitives/tooltip'
import { GENERATE_TRIP_DESCRIPTION_TOOLTIP_COPY } from '@/shared/copy/tooltips'
import { TripDateQuickSelect, TripDateRangePicker, type QuickSelectPreset } from '@/modules/trips'
import { validateDateRange } from '@/modules/trips/utils/dateHelpers'
import { useTrips } from '@/modules/trips/hooks/useTrips'
import { useGenerateTripDescription } from '@/modules/trips/hooks/useGenerateTripDescription'
import { useSubscription, UpgradePrompt } from '@/modules/subscriptions'
import { cn } from '@/shared/utils'
import type { CreateTripDto } from '@/shared/api'

interface CreateTripModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const initialFormData: CreateTripDto = {
  name: '',
  description: '',
  start_date: '',
  end_date: '',
  is_public: false,
  crew_limit: null,
  radio_channel: ''
}

export function CreateTripModal({ open, onOpenChange }: CreateTripModalProps) {
  const { createTrip, isLoading, error } = useTrips()
  const { checkTripLimit, status } = useSubscription()
  const [tripLimit, setTripLimit] = useState<{ allowed: boolean; current: number; limit: number } | null>(null)
  const [formData, setFormData] = useState<CreateTripDto>(initialFormData)
  const [selectedPreset, setSelectedPreset] = useState<QuickSelectPreset>(null)
  const [dateError, setDateError] = useState<string | null>(null)
  const [descriptionSentence, setDescriptionSentence] = useState('')
  const { generate, isGenerating: isGeneratingDescription, error: descriptionGenError, clearError, canUseRanger } =
    useGenerateTripDescription()

  useEffect(() => {
    if (open) {
      const run = async () => {
        const limit = await checkTripLimit()
        setTripLimit(limit)
      }
      run()
    }
  }, [open, checkTripLimit])

  useEffect(() => {
    if (!open) {
      setFormData(initialFormData)
      setSelectedPreset(null)
      setDateError(null)
      setDescriptionSentence('')
    }
  }, [open])

  const handleInputChange = (field: keyof CreateTripDto, value: string | number | null | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (field === 'start_date' || field === 'end_date') {
      setSelectedPreset(null)
    }
    if (field === 'start_date' || field === 'end_date') {
      const startDate = field === 'start_date' ? (value as string) : formData.start_date
      const endDate = field === 'end_date' ? (value as string) : formData.end_date
      if (startDate && endDate) {
        const validation = validateDateRange(startDate, endDate)
        setDateError(validation.isValid ? null : validation.error || null)
      } else {
        setDateError(null)
      }
    }
  }

  const handleGenerateTripDescription = async () => {
    if (!descriptionSentence.trim()) return
    clearError()
    const result = await generate({
      sentence: descriptionSentence,
      tripName: formData.name.trim() || undefined,
      startDate: formData.start_date || undefined,
      endDate: formData.end_date || undefined,
    })
    if (result) {
      handleInputChange('description', result.slice(0, 500))
    }
  }

  const handleQuickSelect = (startDate: string, endDate: string) => {
    setFormData(prev => ({ ...prev, start_date: startDate, end_date: endDate }))
    setDateError(null)
  }

  const handlePresetChange = (preset: QuickSelectPreset) => {
    setSelectedPreset(preset)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (formData.description && formData.description.length > 500) {
      setDateError('Description must be 500 characters or less')
      return
    }
    if (formData.start_date && formData.end_date) {
      const validation = validateDateRange(formData.start_date, formData.end_date)
      if (!validation.isValid) {
        setDateError(validation.error || 'Invalid date range')
        return
      }
    }
    if (!formData.start_date || !formData.end_date) {
      setDateError('Please select trip dates')
      return
    }
    try {
      await createTrip(formData)
      onOpenChange(false)
    } catch {
      // Error surfaced by useTrips
    }
  }

  const atLimit = tripLimit && !tripLimit.allowed
  const showRangerDescriptionEditor = canUseRanger && Boolean(formData.description?.trim())
  const descriptionLabelFor =
    !canUseRanger || showRangerDescriptionEditor ? 'create-trip-description' : 'create-trip-desc-seed'
  const canSubmit =
    !isLoading &&
    !atLimit &&
    formData.name.trim() !== '' &&
    formData.start_date !== '' &&
    formData.end_date !== '' &&
    !dateError

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'max-w-2xl max-h-[90vh] overflow-y-auto bg-earth-50 border-earth-700 text-foreground',
          'max-md:inset-x-0 max-md:left-0 max-md:translate-x-0 max-md:bottom-0 max-md:top-auto max-md:rounded-t-xl max-md:max-h-[90dvh] max-md:max-w-none max-md:pb-[env(safe-area-inset-bottom)]'
        )}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <MapPin className="h-5 w-5" />
            Create Trip
          </DialogTitle>
        </DialogHeader>

        {atLimit && (
          <UpgradePrompt
            variant="error"
            message={`You've reached your trip limit (${tripLimit.current}/${tripLimit.limit} active trips). Upgrade to Pro for unlimited trips.`}
            currentTier={status?.tier || 'free'}
          />
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="create-trip-name" className="flex items-center gap-2">
              <span>Trip Name *</span>
            </Label>
            <Input
              id="create-trip-name"
              value={formData.name}
              onChange={e => handleInputChange('name', e.target.value)}
              placeholder="e.g., Moab Desert Adventure"
              className="mt-1 min-h-[44px]"
              required
            />
          </div>

          <div>
            <Label htmlFor={descriptionLabelFor}>Description</Label>
            {canUseRanger ? (
              <>
                <div className="mt-1 space-y-2">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                    <div className="flex-1 space-y-1">
                      <Label htmlFor="create-trip-desc-seed" className="text-xs font-normal text-muted-foreground">
                        Start from one sentence
                      </Label>
                      <Input
                        id="create-trip-desc-seed"
                        value={descriptionSentence}
                        onChange={e => {
                          clearError()
                          setDescriptionSentence(e.target.value)
                        }}
                        placeholder="e.g., Moab base camp with day runs on Fins and Things"
                        disabled={isLoading || isGeneratingDescription}
                        className="min-h-[44px]"
                      />
                    </div>
                    <div className="flex items-center gap-1 shrink-0 pb-0.5">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleGenerateTripDescription}
                        disabled={
                          !descriptionSentence.trim() || isLoading || isGeneratingDescription
                        }
                        className="text-xs text-earth-700 dark:text-foreground"
                      >
                        {isGeneratingDescription ? (
                          <>
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-3 h-3 mr-1" />
                            Generate description
                          </>
                        )}
                      </Button>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-full p-0.5 text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            aria-label="How does generate description work?"
                          >
                            <HelpCircle className="w-3.5 h-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" align="end" className="max-w-xs">
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {GENERATE_TRIP_DESCRIPTION_TOOLTIP_COPY}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                  {descriptionGenError && (
                    <p className="text-sm text-destructive">{descriptionGenError}</p>
                  )}
                </div>
                {showRangerDescriptionEditor && (
                  <>
                    <Textarea
                      id="create-trip-description"
                      value={formData.description}
                      onChange={e => handleInputChange('description', e.target.value)}
                      placeholder="Edit your trip description..."
                      className="mt-2 min-h-[88px]"
                      rows={4}
                      maxLength={500}
                    />
                    <div
                      className={`text-xs mt-1 ${
                        (formData.description?.length || 0) >= 500
                          ? 'text-destructive'
                          : (formData.description?.length || 0) > 450
                            ? 'text-warning'
                            : 'text-muted-foreground'
                      }`}
                    >
                      {(formData.description?.length || 0)}/500 characters
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <Textarea
                  id="create-trip-description"
                  value={formData.description}
                  onChange={e => handleInputChange('description', e.target.value)}
                  placeholder="Describe your trip..."
                  className="mt-1 min-h-[88px]"
                  rows={4}
                  maxLength={500}
                />
                <div
                  className={`text-xs mt-1 ${
                    (formData.description?.length || 0) >= 500
                      ? 'text-destructive'
                      : (formData.description?.length || 0) > 450
                        ? 'text-warning'
                        : 'text-muted-foreground'
                  }`}
                >
                  {(formData.description?.length || 0)}/500 characters
                </div>
              </>
            )}
          </div>

          <div>
            <Label htmlFor="create-trip-crew">Crew Limit (optional)</Label>
            <Input
              id="create-trip-crew"
              type="number"
              min={1}
              value={formData.crew_limit ?? ''}
              onChange={e => {
                const value = e.target.value
                handleInputChange('crew_limit', value === '' ? null : parseInt(value, 10))
              }}
              placeholder="e.g., 5"
              className="mt-1 min-h-[44px]"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Maximum number of vehicles/crew members. Leave empty for unlimited.
            </p>
          </div>

          <div>
            <Label htmlFor="create-trip-radio">Radio channel (optional)</Label>
            <Input
              id="create-trip-radio"
              placeholder="e.g., Channel 4"
              value={formData.radio_channel ?? ''}
              onChange={e => handleInputChange('radio_channel', e.target.value)}
              className="mt-1 min-h-[44px]"
            />
          </div>

          <div>
            <Label>Trip Dates *</Label>
            <div className="space-y-4 mt-1">
              <TripDateQuickSelect
                onDateSelect={handleQuickSelect}
                selectedPreset={selectedPreset}
                onPresetChange={handlePresetChange}
              />
              <TripDateRangePicker
                startDate={formData.start_date}
                endDate={formData.end_date}
                onStartDateChange={date => handleInputChange('start_date', date)}
                onEndDateChange={date => handleInputChange('end_date', date)}
              />
              {dateError && <p className="text-sm text-destructive">{dateError}</p>}
            </div>
          </div>

          {error && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3">
              <p className="text-sm text-destructive">{typeof error === 'string' ? error : String(error)}</p>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
              className="min-h-[44px]"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit} className="min-h-[44px]">
              {isLoading ? 'Creating...' : 'Create Trip'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
